import { ShapeTextView } from './text/shape-text-view';
import { ShapeTextModel } from './../model/text/shape-text.mdl';
import { Shape, Graphics, Shadow } from '@creately/createjs-module';
import { ShapeRenderView, ITextView, AbstractShapeModel } from 'flux-diagram-composer';
import { ShapeModel } from '../model/shape.mdl';
import { ShapeDisplayView } from './shape-display-view';
import { TransformRenderMethod, IShapeImage } from 'flux-definition';
import { ShapeImageView } from './image/shape-image-view';
import { TiptapShapeTextView } from './text/tiptap-shape-text-view';
import { TiptapShapeDomView } from './text/tiptap-shape-dom-view';

/**
 * The shape view that will be used for canvas rendering.
 * This is the primary shape view that will be used on nucleus
 * to visually represent shapes. This adeheres to the rendering
 * requirements of Diagram Composer platform by extending the ShapeRenderView.
 *
 * This shape will use EaselJS along with its parent DiagramView. The functionality
 * on this class will be dominated by the capabilities of EaselJS.
 *
 * @author hiraash
 * @since 2017-09-11
 */
export class ShapeView extends ShapeRenderView {

    /**
     * This is the easeljs view of the shape that will contain
     * all the peices that show the shape. Every view part of the shape
     * must be added into this container.
     */
    protected container: ShapeDisplayView;

    /**
     * The easeljs representation of the drawing space. Everything
     * will be drawn on this Shape object
     */
    protected shape: Shape;
    protected shadowShape: Shape;

    /**
     * The shape for shape hitArea.
     * hitArea will be drawn from the shape bounds so the the
     * non rectangular shapes can be clicked anywhere inside the bounds.
     */
    protected _hitArea: Shape;

    /**
     * The model associated with this shape.
     * @override
     */
    public get model(): ShapeModel {
        return <ShapeModel>this._model;
    }

    /**
     * Expected hitArea IGraphics instance attached to this shape
     * @override
     */
    public get hitArea(): Graphics {
        if ( this._hitArea ) {
            return this._hitArea.graphics;
        }
    }

    /**
     * Expected output of a shape.
     * @override
     */
    public get renderedShape(): ShapeDisplayView {
        return this.container;
    }

    /**
     * Expected IGraphics instance attached to this shape
     * @override
     */
    public get graphics(): Graphics {
        return this.shape.graphics;
    }

    /**
     * Expected IGraphics instance attached to this shadow shape and
     * and need to draw a graphics to show the shadow.This will be bottom
     * layer of the shape.
     * @override
     */
    public get shadow(): Graphics {
        return this.shadowShape.graphics;
    }

    /**
     * Called when the shape is added.
     * @override
     */
    public initialize() {
        this.container = new ShapeDisplayView( this.id, this.model.type );

        this.shape = new Shape();
        this.container.convertToCursorDrawable( this.shape );
        this.shape.name = this.id;

        this.shadowShape = new Shape();
        if (( this.model as AbstractShapeModel ).style.shadow ) {
            this.shadowShape.shadow = new Shadow(
                ( this.model as AbstractShapeModel ).style.shadow.color,
                ( this.model as AbstractShapeModel ).style.shadow.offsetX,
                ( this.model as AbstractShapeModel ).style.shadow.offsetY,
                ( this.model as AbstractShapeModel ).style.shadow.blur );
            this.container.addChild( this.shadowShape, this.shape );
        } else {
            this.container.addChild( this.shape );
        }
    }

    /**
     * This method ultimately redraws this shape on the given
     * drawing space. The method would ensure that all data changes
     * are translated into the drawing space as intended by this shape.
     * This means the full shape will be up-to-date on the drawing space
     * when this method completes until the next data change on the model.
     * @param change A json with all the changes that happened to the model.
     *          Can be used to check which props changed or what changes happened.
     * @override
     */
    public render( change?: any ) {
        // NOTE: If the rendering method is switched,
        // the text view should be recreated, so it's removed here so that it will be recreated
        if (  change?.$set ) {
            const regex = /texts\.([^\.]+)\.rendering$/;
            Object.keys( change.$set || {})
                .filter( k => regex.test( k ))
                .forEach( k => {
                    const textId = k.split( '.' )[ 1 ];
                    const textView = this._texts.find( tv => tv.name === textId );
                    if ( textView ) {
                        this.removeText( textView );
                    }
                });
        }
        super.render( change );
        if ( this.model.transformSettings.scaleRender === TransformRenderMethod.redraw ) {
            this.shape.setBounds( 0, 0, this.model.drawWidth, this.model.drawHeight );
            this.shadowShape.setBounds( 0, 0, this.model.drawWidth, this.model.drawHeight );
        } else {
            const bound = this.model.defaultBounds;
            this.shape.setBounds( bound.x, bound.y, bound.width, bound.height );
            this.shadowShape.setBounds( bound.x, bound.y, bound.width, bound.height );
        }
    }

    public beginDraw() {
        this.beginDrawHitArea();
        super.beginDraw();
    }

    /**
     * This function sets a hitArea for the shape
     */
    public beginDrawHitArea() {
        if ( this.model.disableHitArea ) {
            return;
        }
        if ( !this._hitArea ) {
            this._hitArea = new Shape();
            this.shape.hitArea = this._hitArea;
        }
        this.hitArea.clear();
        const bounds = this.model.bounds;
        this.hitArea
            .beginFill( '#ffffff' ) // Just a random color for the hitarea to work. It will not be visible
            .drawRect( 0, 0, bounds.width * Math.sign( this.model.scaleX ),
                bounds.height * Math.sign( this.model.scaleY ) );
        this._hitArea.setBounds( 0, 0, bounds.width, bounds.height );
    }

    public endDraw() {
        super.endDraw();
        if ( this.hitArea ) {
            this.hitArea.endFill();
        }
    }

    /**
     * This function creates a text view from the given
     * text model and adds it to the container and text views array
     * @param text: ShapeTextModel
     * @override
     */
    protected addText( text: ShapeTextModel ) {
        // Editable text models do not have a text view
        if ( text.displayableTextId ) {
            return;
        } else if ( text.rendering === 'tiptapCanvas' ) {
            const textView = new TiptapShapeTextView( text, this.model.id );
            this.container.addChild( textView );
            this._texts.push( textView );
        } else if ( text.rendering === 'dom' ) {
            const textView = new TiptapShapeDomView( text, this.model.id );
            this.container.addChild( textView );
            this._texts.push( textView );
        } else {
            const textView = new ShapeTextView( text );
            this.container.addChild( textView );
            this._texts.push( textView );
        }
    }

    /**
     * This removes the text view from the text view list. This also
     * must ensure it is removed from the rendering.
     * @param text The text view to remove.
     */
    protected removeText( text: ITextView ) {
        super.removeText( text );
        this.renderedShape.removeChild( text as any );
    }

    protected addImage( imageModel: IShapeImage ) {
        const imageView = new ShapeImageView( imageModel );
        imageView.initialize();
        this._images.push( imageView );
        this.container.addChild( imageView.renderedImage );
        if ( imageView.imageOutline ) {
            this.container.addChild( imageView.imageOutline );
        }
    }

    /**
     * Moves the shape to the given location
     * @param x the position on the canvas to move to
     * @param y the position on the canvas to move to
     * @override
     */
    protected move( x: number, y: number ) {
        this.shape.x = x;
        this.shape.y = y;
        this.shadowShape.x = x;
        this.shadowShape.y = y;
    }

    /**
     * Scales the shape in height.
     * @param scale The scale multiplier
     */
    protected scaleHeight( scale: number ) {
        this.shape.scaleY = scale;
        this.shadowShape.scaleY = scale;
    }

    /**
     * Scales the shape in width.
     * @param scale The scale multiplier
     */
    protected scaleWidth( scale: number ) {
        this.shape.scaleX = scale;
        this.shadowShape.scaleX = scale;
    }

    /**
     * Rotates the shape to the given angle
     * @param angle The angle to rotate to.
     */
    protected rotate( angle: number ) {
        this.shape.rotation = angle;
        this.shadowShape.rotation = angle;
    }

}
